import { useCallback, useEffect } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { isBoolean, useDisclosure, usePersistentState } from "@ir/ui";
import { Sidebar } from "./Sidebar";
import { Topbar } from "./Topbar";

const SIDEBAR_STORAGE_KEY = "ir-admin-sidebar-collapsed";

/** Sidebar + topbar frame around every authenticated page. */
export function AdminShell() {
  const nav = useDisclosure(false);
  const location = useLocation();

  // Remembered per browser: someone who works on a laptop all day should not
  // have to re-collapse the rail on every visit.
  const [collapsed, setCollapsed] = usePersistentState(
    SIDEBAR_STORAGE_KEY,
    false,
    isBoolean,
  );

  const toggleCollapsed = useCallback(
    () => setCollapsed((current) => !current),
    [setCollapsed],
  );

  // Close the mobile drawer and reset scroll whenever the route changes.
  useEffect(() => {
    nav.onClose();
    window.scrollTo({ top: 0 });
  }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="flex min-h-dvh bg-canvas">
      <Sidebar
        open={nav.open}
        onClose={nav.onClose}
        collapsed={collapsed}
        onToggleCollapsed={toggleCollapsed}
      />

      <div className="flex min-w-0 flex-1 flex-col">
        <Topbar onOpenNav={nav.onOpen} />

        <motion.main
          key={location.pathname}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.28, ease: [0.16, 1, 0.3, 1] }}
          className="mx-auto w-full max-w-[100rem] flex-1 px-4 py-6 sm:px-6 sm:py-8"
        >
          <Outlet />
        </motion.main>
      </div>
    </div>
  );
}
